/**
 * The vertical rhythm both dashboard pages share.
 *
 * It used to hold four things: the rail, the rail pages' own top strip, the
 * padding AppLayout withheld on those routes, and the spacing between the
 * blocks a dashboard is made of. The first three are gone with the sidebar —
 * one navigation now, and AppLayout's inset applies to every route — and this
 * is what was left.
 *
 * Kept rather than deleted, because that last thing is real and shared. My Day
 * and the admin home both pass a run of sibling blocks as children and rely on
 * the gap between them; inlining it would put the same wrapper in two pages
 * and leave the next dashboard to remember it. A component for one class is
 * thin, and two copies of one class that must agree is worse.
 *
 * It also caps the width, which the rail used to do by accident. The shell
 * runs `w-full px-6` with no maximum -- right for the screens carrying 33- and
 * 18-column tables, and wrong for a grid of cards, which the 224px rail was
 * quietly keeping in check. Without a cap a 2560px monitor gave three columns
 * of 837px each, so the reclaimed space went entirely into stretching cards
 * that were already wide enough.
 *
 * 1600px is close to what these pages actually had: at 1920 the rail left them
 * 1656px, so the cap changes almost nothing there and only bites on screens
 * wider than any they were designed against. Below roughly 1650px it does not
 * apply at all, and a laptop gets the full reclaimed width.
 *
 * On the dashboards alone, deliberately. Every other page still runs edge to
 * edge, because a table that has to scroll sideways is a worse outcome than a
 * wide one.
 */
export default function DashboardShell({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className="max-w-[1600px] space-y-5">{children}</div>;
}
